import React from "react";
import { Link } from "react-router-dom";

import { cn } from "@/utils/cn";

import Icon from "../icon/icon";
import Stack from "../stack/stack";
import Text from "../text/text";

interface BlankslateProps {
  className?: string;
  iconSrc?: string;
  linkSrc?: string;
  linkText?: string | React.ReactElement;
  /**
   * Optional action element rendered below the title/subtitle — pass a
   * Button, a Link, or any other component. Takes precedence over the
   * `linkSrc`/`linkText` pair when both are supplied.
   */
  action?: React.ReactNode;
  subtitle?: string | React.ReactElement;
  title: string | React.ReactElement;
}

/**
 * Blankslate
 *
 * A component to use for when a list / page has no data to display.
 */
const Blankslate = (props: BlankslateProps) => {
  const { action, className, iconSrc, linkSrc, linkText, subtitle, title } =
    props;

  return (
    <Stack space={4} className={cn("py-10", className)} alignItems="center">
      {iconSrc && (
        <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-800">
          <Icon
            src={iconSrc}
            className="size-6 text-gray-600 dark:text-gray-600"
          />
        </div>
      )}

      <Stack space={2}>
        {title && subtitle ? (
          <>
            <Text size="2xl" weight="bold" align="center">
              {title}
            </Text>

            <Text theme="muted" size="md" align="center" className="leading-6">
              {subtitle}
            </Text>
          </>
        ) : (
          <Text theme="muted" align="center">
            {title}
          </Text>
        )}
      </Stack>

      {action ?? (linkSrc && linkText && (
        <Link className="text-center" to={linkSrc}>
          <Text theme="primary" size="sm" className="hover:underline">
            {linkText}
          </Text>
        </Link>
      ))}
    </Stack>
  );
};

export default Blankslate;
